import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import ApiInspector from '../../components/ApiInspector';
import ComboFiltro from '../../components/ComboFiltro';
import { apiDownload, apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { useFiltriApplicati } from '../../hooks/useFiltriApplicati';
import BopLoaderOverlay from '../../components/BopLoaderOverlay';
import {
  BustaGo, DataSuGo, GruppoTotali, PeriodoGo, RaggruppaGo, RispostaDaCollegare,
  RispostaTotali, StatoGo, VistaGo, applicaPeriodo, dataIt, descriviPeriodo,
  etichettaDataRiferimento, euro, num,
} from '../../lib/gestionaleOpen';
import FiltroPeriodoGo, { periodoIniziale } from './FiltroPeriodoGo';
import { API_DOCS } from './AnalisiCommercialeGo.api';
import ModaleDocumentiGo, { stessoMese } from './ModaleDocumentiGo';
import { BottoneCercaGo, InvitoCercaGo } from './CercaGo';

/**
 * Analisi costi e ricavi sui dati di Gestionale Open.
 *
 * Le due pagine — "Costi - fornitori" e "Vendita - clienti" — sono la stessa
 * analisi su due cicli. Cambia la controparte, la vista dell'API e un paio di
 * regole (le autofatture sono un problema del solo ciclo attivo, l'evaso del
 * solo ordinato): tenerle in due file gemelli significherebbe correggere ogni
 * bug due volte.
 *
 * Due tab. La prima, aperta di default, elenca le fatture con una parte non
 * attribuita a commessa: sono i casi da sistemare in GO, e devono essere la
 * prima cosa che si vede. La seconda e' l'analisi per controparte.
 *
 * ⚠️ Niente margine qui — ricavi e costi si leggono separatamente; il margine
 * per commessa sta nella pagina "Analisi commesse". (La vecchia scelta di non
 * fare analisi per commessa nasceva da un'attribuzione dei costi misurata al
 * 2%: era un errore delle viste GO, che ignoravano i movimenti commessa.
 * Corrette l'11/09/2026, i costi 2026 di arc_solu sono attribuiti al 94%.)
 *
 * Sulle fatture una riga ripartita su piu' commesse arriva come piu' record,
 * uno per movimento: la chiave di riga include id_movimento_commessa.
 *
 * ⚠️ Una societa' per volta, SUPERADMIN compreso. I codici cliente e fornitore
 * sono locali a ciascun database GO: lo stesso cliente puo' avere codici
 * diversi in societa' diverse. Sommare fra societa' darebbe un numero privo di
 * significato, quindi non si fa.
 */

export type CicloGo = 'attivo' | 'passivo';

type TabGo = 'da-collegare' | 'analisi';

interface Props {
  ciclo: CicloGo;
  titolo: string;
  introduzione: string;
}

interface OpzioneVista { valore: VistaGo; etichetta: string; nota: string }

const VISTE_PER_CICLO: Record<CicloGo, OpzioneVista[]> = {
  attivo: [
    { valore: 'ricavi', etichetta: 'Fatturato', nota: 'Fatture di vendita' },
    { valore: 'ordini-cliente', etichetta: 'Ordinato', nota: 'Ordini di vendita' },
  ],
  passivo: [
    { valore: 'costi-fornitore', etichetta: 'Costi', nota: 'Fatture di acquisto' },
    { valore: 'ordini-fornitore', etichetta: 'Ordinato', nota: 'Ordini a fornitore' },
  ],
};

/** La controparte cambia nome, e il raggruppamento per controparte con lei. */
const CONTROPARTE: Record<CicloGo, { chiave: RaggruppaGo; etichetta: string; plurale: string }> = {
  attivo: { chiave: 'cliente', etichetta: 'Cliente', plurale: 'clienti' },
  passivo: { chiave: 'fornitore', etichetta: 'Fornitore', plurale: 'fornitori' },
};

const RAGGRUPPAMENTI_COMUNI: Array<{ valore: RaggruppaGo; etichetta: string }> = [
  { valore: 'mese', etichetta: 'Mese' },
  { valore: 'anno', etichetta: 'Anno' },
  { valore: 'tipo_documento', etichetta: 'Tipo documento' },
  { valore: 'articolo', etichetta: 'Articolo' },
  { valore: 'commessa', etichetta: 'Commessa' },
];

type ModoCommessa = 'tutte' | 'solo' | 'senza';

const ANNO_CORRENTE = new Date().getFullYear();

// Date su cui l'API GO sa contare il periodo, per vista (la prima e' il
// default dell'API). La registrazione esiste solo sul ciclo passivo; la
// competenza di bilancio solo sulle fatture.
const DATE_PER_VISTA: Record<VistaGo, DataSuGo[]> = {
  ricavi: ['documento', 'competenza'],
  'ordini-cliente': ['documento'],
  'costi-fornitore': ['registrazione', 'documento', 'competenza'],
  'ordini-fornitore': ['registrazione', 'documento'],
};

/** Il periodo con una data valida per la vista: se non lo e', quella di default. */
function periodoPerVista(periodo: PeriodoGo, vista: VistaGo): PeriodoGo {
  const ammesse = DATE_PER_VISTA[vista];
  return periodo.dataSu && ammesse.includes(periodo.dataSu) ? periodo : { ...periodo, dataSu: ammesse[0] };
}

/** Stati della risposta GO che non sono dati: uguali nelle due tab. */
function AvvisiStatoGo({ societaId, stato, avviso }: { societaId: string; stato: StatoGo; avviso: string }) {
  if (!societaId) {
    return (
      <div className="bop-empty-state">
        Seleziona una società. I dati GO si leggono una società per volta: i codici
        cliente e fornitore sono locali a ciascun database, e sommarli fra società
        darebbe un numero privo di significato.
      </div>
    );
  }
  if (stato === 'NON_MAPPATA') {
    return (
      <div className="bop-empty-state">
        Questa società non ha un database di Gestionale Open associato, quindi non ha
        dati GO. Si imposta in <strong>Impostazioni Generali → Società del gruppo</strong>,
        campo «Database Gestionale Open».
      </div>
    );
  }
  if (stato === 'NON_DISPONIBILE') {
    return (
      <div className="bop-empty-state">
        <div className="bop-empty-state__title">Dati non ancora disponibili per questa società</div>
        <div className="bop-empty-state__text">
          Le viste di analisi non sono ancora state create sul database GO di questa
          società. Non è un guasto: è lo stato del rollout, e non è lavoro di BOP.
          {avviso && <><br />{avviso}</>}
        </div>
      </div>
    );
  }
  return null;
}

// ── Tab "Da collegare" ─────────────────────────────────────────────────────────

interface PropsDaCollegare {
  ciclo: CicloGo;
  societaId: string;
  periodo: PeriodoGo;
  escludiAutofatture: boolean;
  /** Codice cliente/fornitore scelto nel filtro nominativo ('' = tutti) */
  controparteCodice: string;
  /** Cresce a ogni «Cerca»: rilegge anche a filtri invariati */
  versione: number;
  /** False finche' l'utente non ha premuto «Cerca»: la tab resta vuota */
  cercato: boolean;
}

/**
 * Fatture con almeno una parte non attribuita a commessa, una riga per
 * fattura. Solo fatture: sugli ordini GO non genera movimenti commessa, e il
 * collegamento si fa sul documento che entra in contabilita'.
 */
function TabDaCollegare({ ciclo, societaId, periodo, escludiAutofatture, controparteCodice, versione, cercato }: PropsDaCollegare) {
  const controparte = CONTROPARTE[ciclo];
  const sulCicloAttivo = ciclo === 'attivo';
  const vista: VistaGo = sulCicloAttivo ? 'ricavi' : 'costi-fornitore';

  const [risposta, setRisposta] = useState<RispostaDaCollegare | null>(null);
  const [stato, setStato] = useState<StatoGo>('OK');
  const [avviso, setAvviso] = useState('');
  const [errore, setErrore] = useState('');
  const [caricando, setCaricando] = useState(false);

  const [aperto, setAperto] = useState<GruppoTotali | null>(null);

  const filtri = useCallback(() => {
    const p = applicaPeriodo(new URLSearchParams({ societaId, vista }), periodoPerVista(periodo, vista));
    if (sulCicloAttivo && escludiAutofatture) p.set('escludi_autofatture', 'true');
    if (controparteCodice) p.set(controparte.chiave, controparteCodice);
    return p;
  }, [societaId, vista, periodo, sulCicloAttivo, escludiAutofatture, controparteCodice, controparte]);

  useEffect(() => {
    if (!cercato || !societaId) { setRisposta(null); setStato('OK'); return undefined; }
    let annullato = false;
    (async () => {
      setCaricando(true);
      setErrore('');
      setAvviso('');
      setAperto(null);
      try {
        // @api GET /api/bop-go/dati-commerciali/da-collegare?societaId=1&vista=costi-fornitore&anno=2026 -> {"success":true,"stato":"OK","data":{"vista":"costi-fornitore","data_riferimento":"DATA_REGISTRAZIONE","documenti_periodo":316,"importo_periodo":1424190.35,"importo_da_attribuire":83832.57,"data":[{"codice":3446,"etichetta":"CAPLAB SRL","codice_documento":"FATA","numero_documento":"2","importo":"100571.85","importo_con_commessa":"0.00","importo_senza_commessa":"100571.85"}]}}
        const res = await apiFetch<BustaGo<RispostaDaCollegare>>(`/api/bop-go/dati-commerciali/da-collegare?${filtri().toString()}`);
        if (annullato) return;
        setStato(res.stato);
        setRisposta(res.data);
        if (res.stato !== 'OK' && res.error) setAvviso(res.error);
      } catch (err) {
        if (annullato) return;
        setErrore(err instanceof Error ? err.message : 'Errore nel recupero delle fatture da GO');
        setRisposta(null);
      } finally {
        if (!annullato) setCaricando(false);
      }
    })();
    return () => { annullato = true; };
  }, [cercato, societaId, filtri, versione]);

  const colonne = useMemo<BopExcelColumn<GruppoTotali>[]>(() => [
    {
      key: 'data',
      header: 'Data',
      accessor: (d) => d.prima_data ?? '',
      render: (d) => dataIt(d.prima_data),
      width: '110px',
    },
    {
      key: 'documento',
      header: 'Documento',
      accessor: (d) => `${d.codice_documento ?? ''} ${d.numero_documento ?? ''}`.trim(),
      render: (d) => (
        <span className="bop-analisi-go__gruppo">
          <span className="bop-analisi-go__gruppo-nome">
            {d.descrizione_documento ?? d.codice_documento ?? '—'} n. {d.numero_documento ?? '—'}
          </span>
          <span className="bop-analisi-go__gruppo-codice">{d.codice_documento}</span>
        </span>
      ),
      filter: { type: 'text' },
      width: '240px',
    },
    {
      key: 'controparte',
      header: controparte.etichetta,
      accessor: (d) => d.etichetta ?? '',
      filter: { type: 'text' },
    },
    {
      key: 'importo',
      header: 'Importo fattura',
      accessor: (d) => num(d.importo),
      render: (d) => euro(d.importo),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      width: '140px',
    },
    {
      key: 'attribuito',
      header: 'Attribuito',
      accessor: (d) => num(d.importo_con_commessa),
      render: (d) => euro(d.importo_con_commessa),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      width: '140px',
    },
    {
      key: 'da_attribuire',
      header: 'Da attribuire',
      accessor: (d) => num(d.importo_senza_commessa),
      render: (d) => euro(d.importo_senza_commessa),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      filter: { type: 'number' },
      width: '140px',
    },
  ], [controparte]);

  const quota = risposta && risposta.importo_periodo
    ? Math.round((risposta.importo_da_attribuire / risposta.importo_periodo) * 100)
    : null;

  return (
    <>
      <BopLoaderOverlay visibile={caricando} testo="Lettura delle fatture da Gestionale Open…" />
      <AvvisiStatoGo societaId={societaId} stato={stato} avviso={avviso} />

      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

      {societaId && stato === 'OK' && risposta && (
        <>
          <div className="bop-analisi-go__kpi">
            <div className="bop-analisi-go__stat bop-analisi-go__stat--attenzione">
              <div className="bop-analisi-go__stat-label">Fatture da collegare</div>
              <div className="bop-analisi-go__stat-value">{risposta.data.length}</div>
              <div className="bop-analisi-go__stat-sub">su {risposta.documenti_periodo} fatture del periodo</div>
            </div>
            <div className="bop-analisi-go__stat">
              <div className="bop-analisi-go__stat-label">Importo da attribuire</div>
              <div className="bop-analisi-go__stat-value">{euro(risposta.importo_da_attribuire)}</div>
              <div className="bop-analisi-go__stat-sub">
                {quota !== null ? `${quota}% del totale ${sulCicloAttivo ? 'ricavi' : 'costi'}` : '—'}
              </div>
            </div>
          </div>

          <p className="bop-analisi-go__nota">
            Fatture con almeno una parte non attribuita a commessa: né movimento commessa in GO,
            né commessa sulla riga o sulla testata. Si collegano in GO dalla maschera
            «Caricamento commesse da documenti» e da qui spariscono da sole. Clic su una
            fattura per vederne le righe.
            {risposta.fuori_conto_economico && risposta.fuori_conto_economico.documenti > 0 && (
              <>
                {' '}Escluse <strong>{risposta.fuori_conto_economico.documenti}</strong> fatture
                ({euro(risposta.fuori_conto_economico.importo)}) registrate senza conti economici —
                {sulCicloAttivo
                  ? ' ad esempio fatture da emettere già rilevate, anticipi o SAL, il cui ricavo arriva da altre scritture'
                  : " ad esempio su «fatture da ricevere», con il costo già nell'esercizio precedente"}
                : non muovono né il conto economico né le commesse.
              </>
            )}
          </p>

          <BopExcelTable<GruppoTotali>
            data={risposta.data}
            columns={colonne}
            rowKey={(d) => String(d.codice)}
            loading={caricando}
            onRowClick={(d) => { if (d.codice !== null && d.codice !== undefined) setAperto(d); }}
            initialSort={{ key: 'da_attribuire', dir: 'desc' }}
            storageKey={`da-collegare-go-${ciclo}`}
            emptyState={<div className="bop-empty-state">Tutte le fatture del periodo sono collegate a una commessa</div>}
          />
        </>
      )}

      {caricando && !risposta && <div className="bop-empty-state">Lettura delle fatture…</div>}

      {aperto && (
        <ModaleDocumentiGo
          titolo={aperto.etichetta ?? controparte.etichetta}
          filtri={filtri()}
          documentoIniziale={aperto}
          conDaAttribuire
          onClose={() => setAperto(null)}
        />
      )}
    </>
  );
}

// ── Pagina ─────────────────────────────────────────────────────────────────────

export default function AnalisiCommercialeGo({ ciclo, titolo, introduzione }: Props) {
  const { societaId: societaSel, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const viste = VISTE_PER_CICLO[ciclo];
  const controparte = CONTROPARTE[ciclo];

  // Si apre sulle fatture da collegare: sono i casi da gestire.
  const [tab, setTab] = useState<TabGo>('da-collegare');

  // Filtri: i controlli cambiano la bozza (…Sel), le letture usano i valori
  // applicati con «Cerca» (stessi nomi di sempre, piu' sotto)
  const [vistaSel, setVista] = useState<VistaGo>(viste[0].valore);
  const [raggruppaSel, setRaggruppa] = useState<RaggruppaGo>(controparte.chiave);
  const [periodoSel, setPeriodo] = useState<PeriodoGo>(() => periodoIniziale());
  const [modoCommessaSel, setModoCommessa] = useState<ModoCommessa>('tutte');
  const [importoMinimoSel, setImportoMinimo] = useState('');
  // Le autofatture non sono ricavi: sono la contropartita di acquisti, e in
  // certi mesi pesano per meta' del fatturato apparente. Escluse di default,
  // ma l'interruttore resta visibile e la loro quota e' sempre esposta.
  const [escludiAutofattureSel, setEscludiAutofatture] = useState(true);
  // Filtro per nominativo: codice GO del cliente/fornitore ('' = tutti). Vale
  // solo per la societa' in cui e' stato scelto: i codici GO sono per database
  const [sceltaControparte, setSceltaControparte] = useState({ societaId: '', codice: '' });
  const controparteSel = sceltaControparte.societaId === societaSel ? sceltaControparte.codice : '';

  const { applicati, versione, applica, modificati, cercato } = useFiltriApplicati({
    societaId: societaSel, vista: vistaSel, raggruppa: raggruppaSel, periodo: periodoSel,
    modoCommessa: modoCommessaSel, importoMinimo: importoMinimoSel,
    escludiAutofatture: escludiAutofattureSel, controparteCodice: controparteSel,
  }, societaSel);
  const {
    societaId, vista, raggruppa, periodo, modoCommessa, importoMinimo, escludiAutofatture, controparteCodice,
  } = applicati;

  const [controparti, setControparti] = useState<Array<{ id: string; nome: string }>>([]);

  const [totali, setTotali] = useState<RispostaTotali | null>(null);
  const [stato, setStato] = useState<StatoGo>('OK');
  const [avviso, setAvviso] = useState('');
  const [errore, setErrore] = useState('');
  const [caricando, setCaricando] = useState(false);

  const [gruppoAperto, setGruppoAperto] = useState<GruppoTotali | null>(null);

  const sulCicloAttivo = ciclo === 'attivo';
  const suOrdinato = vista === 'ordini-cliente' || vista === 'ordini-fornitore';

  const raggruppamenti = useMemo(
    () => [{ valore: controparte.chiave, etichetta: controparte.etichetta }, ...RAGGRUPPAMENTI_COMUNI],
    [controparte]
  );

  /** Filtri comuni a totali e righe. Il database non c'e': lo mette il backend. */
  const filtriComuni = useCallback(() => {
    const p = applicaPeriodo(new URLSearchParams({ societaId, vista }), periodoPerVista(periodo, vista));
    if (modoCommessa === 'solo') p.set('solo_commessa', 'true');
    if (modoCommessa === 'senza') p.set('senza_commessa', 'true');
    if (importoMinimo) p.set('importo_minimo', importoMinimo);
    if (sulCicloAttivo && escludiAutofatture) p.set('escludi_autofatture', 'true');
    if (controparteCodice) p.set(controparte.chiave, controparteCodice);
    return p;
  }, [societaId, vista, periodo, modoCommessa, importoMinimo, sulCicloAttivo, escludiAutofatture, controparteCodice, controparte]);

  // Elenco nominativi per il filtro: i clienti/fornitori con movimenti nel
  // periodo, letti da GO con lo stesso aggregato della tabella. Sulla tab
  // "Da collegare" la vista e' sempre quella delle fatture.
  const vistaElenco: VistaGo = tab === 'analisi' ? vista : viste[0].valore;
  const vistaElencoSel: VistaGo = tab === 'analisi' ? vistaSel : viste[0].valore;
  useEffect(() => {
    if (!societaId) { setControparti([]); return undefined; }
    let annullato = false;
    (async () => {
      try {
        const p = applicaPeriodo(
          new URLSearchParams({ societaId, vista: vistaElenco, raggruppa: controparte.chiave, limit: '20000' }),
          periodoPerVista(periodo, vistaElenco),
        );
        if (sulCicloAttivo && escludiAutofatture) p.set('escludi_autofatture', 'true');
        // @api GET /api/bop-go/dati-commerciali/totali?societaId=1&vista=costi-fornitore&raggruppa=fornitore&anno=2026&limit=20000 -> {"success":true,"stato":"OK","data":{"data":[{"codice":"string","etichetta":"string"}]}}
        const res = await apiFetch<BustaGo<RispostaTotali>>(`/api/bop-go/dati-commerciali/totali?${p.toString()}`);
        if (annullato) return;
        const elenco = (res.stato === 'OK' ? res.data?.data ?? [] : [])
          .filter((g) => g.codice)
          .map((g) => ({ id: String(g.codice), nome: g.etichetta ? `${g.etichetta} (${g.codice})` : String(g.codice) }))
          .sort((x, y) => x.nome.localeCompare(y.nome, 'it'));
        setControparti(elenco);
      } catch (err) {
        if (annullato) return;
        console.error('[AnalisiCommercialeGo][controparti]', err);
        setControparti([]);
      }
    })();
    return () => { annullato = true; };
  }, [societaId, vistaElenco, periodo, sulCicloAttivo, escludiAutofatture, controparte]);

  const caricaTotali = useCallback(async () => {
    // La tab "Da collegare" ha la sua chiamata: questa serve solo all'analisi.
    if (tab !== 'analisi') return;
    if (!cercato || !societaId) { setTotali(null); setStato('OK'); return; }
    setCaricando(true);
    setErrore('');
    setAvviso('');
    setGruppoAperto(null);
    try {
      const p = filtriComuni();
      p.set('raggruppa', raggruppa);
      // @api GET /api/bop-go/dati-commerciali/totali?societaId=1&vista=ricavi&raggruppa=cliente&anno=2026 -> {"success":true,"stato":"OK","data":{"vista":"ricavi","raggruppa":"cliente","data_riferimento":"DATA_DOCUMENTO","database":"arc_solu","count":3,"totale_complessivo":{"righe":4642,"documenti":1089,"importo":1984464.22,"importo_con_commessa":1360376.85,"importo_autofatture":0},"data":[{"codice":"00000011","etichetta":"ESSELUNGA SPA","righe":71,"documenti":56,"importo":824490.73}]}}
      const res = await apiFetch<BustaGo<RispostaTotali>>(`/api/bop-go/dati-commerciali/totali?${p.toString()}`);
      setStato(res.stato);
      setTotali(res.data);
      if (res.stato !== 'OK' && res.error) setAvviso(res.error);
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Errore nel recupero dei dati da GO');
      setTotali(null);
    } finally {
      setCaricando(false);
    }
  // versione: «Cerca» rilegge anche a filtri invariati
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, cercato, societaId, raggruppa, filtriComuni, versione]);

  useEffect(() => { void caricaTotali(); }, [caricaTotali]);

  /** Drill-down in modale: i documenti del gruppo, col gruppo come filtro. */
  const filtriGruppo = useCallback((gruppo: GruppoTotali) => {
    const p = filtriComuni();
    // Su `anno` e `mese` il periodo e' gia' nei filtri comuni e l'API non ha un
    // filtro per mese: il mese lo filtra il modale sulle date dei documenti.
    if (gruppo.codice && raggruppa !== 'anno' && raggruppa !== 'mese') {
      p.set(raggruppa, gruppo.codice);
    }
    return p;
  }, [filtriComuni, raggruppa]);

  const filtroMese = useMemo(() => {
    if (raggruppa !== 'mese' || !gruppoAperto) return undefined;
    const codice = gruppoAperto.codice;
    return (d: GruppoTotali) => stessoMese(codice, d.prima_data);
  }, [raggruppa, gruppoAperto]);

  // ── Export Excel ──────────────────────────────────────────────────────────
  // Due file: la tabella dei totali e tutte le righe che la compongono. Stessi
  // filtri della pagina; il file lo genera il backend, che rilegge da GO. I
  // filtri di colonna della tabella (lato browser) non entrano nell'export.
  const [esportando, setEsportando] = useState<'totali' | 'righe' | null>(null);
  const exportPossibile = !!societaId && stato === 'OK' && !!totali && !caricando && !esportando;

  const esporta = useCallback(async (tipo: 'totali' | 'righe') => {
    setEsportando(tipo);
    setErrore('');
    try {
      const p = filtriComuni();
      p.set('raggruppa', raggruppa);
      const nomeFile = `${titolo.toLowerCase().replace(/[^a-z0-9]+/g, '-')}_${descriviPeriodo(periodo)}_${tipo}.xlsx`;
      if (tipo === 'totali') {
        // @api GET /api/bop-go/dati-commerciali/export/totali?societaId=1&vista=ricavi&raggruppa=cliente&anno=2026 -> file xlsx
        await apiDownload(`/api/bop-go/dati-commerciali/export/totali?${p.toString()}`, nomeFile);
      } else {
        // @api GET /api/bop-go/dati-commerciali/export/righe?societaId=1&vista=ricavi&raggruppa=cliente&anno=2026 -> file xlsx
        await apiDownload(`/api/bop-go/dati-commerciali/export/righe?${p.toString()}`, nomeFile);
      }
    } catch (err) {
      setErrore(`Export Excel non riuscito: ${err instanceof Error ? err.message : 'errore sconosciuto'}`);
    } finally {
      setEsportando(null);
    }
  }, [filtriComuni, raggruppa, titolo, periodo]);

  // ── Colonne dell'aggregato ────────────────────────────────────────────────
  const colonne = useMemo<BopExcelColumn<GruppoTotali>[]>(() => {
    const etichettaGruppo = raggruppamenti.find(r => r.valore === raggruppa)?.etichetta ?? 'Gruppo';
    const base: BopExcelColumn<GruppoTotali>[] = [
      {
        key: 'etichetta',
        header: etichettaGruppo,
        accessor: (r) => r.etichetta ?? r.codice ?? '',
        render: (r) => (
          <span className="bop-analisi-go__gruppo">
            <span className="bop-analisi-go__gruppo-nome">{r.etichetta ?? r.codice ?? '(non indicato)'}</span>
            {r.codice && r.etichetta && <span className="bop-analisi-go__gruppo-codice">{r.codice}</span>}
          </span>
        ),
        filter: { type: 'text' },
      },
      {
        key: 'importo',
        header: 'Importo',
        accessor: (r) => num(r.importo),
        render: (r) => euro(r.importo),
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        filter: { type: 'number' },
        width: '140px',
      },
      {
        key: 'documenti',
        header: 'Documenti',
        accessor: (r) => r.documenti,
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '110px',
      },
      {
        key: 'righe',
        header: 'Righe',
        accessor: (r) => r.righe,
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '90px',
        defaultHidden: true,
      },
      {
        key: 'con_commessa',
        header: 'Su commessa',
        accessor: (r) => num(r.importo_con_commessa),
        render: (r) => euro(r.importo_con_commessa),
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '140px',
      },
      {
        key: 'periodo',
        header: 'Periodo',
        accessor: (r) => r.prima_data ?? '',
        render: (r) => `${dataIt(r.prima_data)} – ${dataIt(r.ultima_data)}`,
        width: '190px',
        defaultHidden: true,
      },
    ];

    // L'evaso esiste solo sull'ordinato. C'e' un'anomalia nota per cui talvolta
    // supera l'ordinato: si mostra com'e', non si corregge ne' si nasconde.
    if (suOrdinato) {
      base.splice(2, 0, {
        key: 'evaso',
        header: 'Evaso',
        accessor: (r) => num(r.importo_evaso),
        render: (r) => euro(r.importo_evaso),
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '140px',
      });
    }

    if (sulCicloAttivo && !escludiAutofatture) {
      base.push({
        key: 'autofatture',
        header: 'di cui autofatture',
        accessor: (r) => num(r.importo_autofatture),
        render: (r) => euro(r.importo_autofatture),
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '160px',
      });
    }

    return base;
  }, [raggruppa, raggruppamenti, suOrdinato, sulCicloAttivo, escludiAutofatture]);

  // ── Avvisi ────────────────────────────────────────────────────────────────
  const annoParziale = !periodo.intervallo && periodo.anno === String(ANNO_CORRENTE);
  const complessivo = totali?.totale_complessivo;
  const importoTotale = num(complessivo?.importo);
  const quotaCommessa = importoTotale
    ? Math.round((num(complessivo?.importo_con_commessa) / importoTotale) * 100)
    : null;

  return (
    <Layout title={titolo}>
      <div className="bop-page-header">
        <h1 className="bop-page-title">{titolo}</h1>
        {tab === 'analisi' && (
          <div className="bop-flex">
            <button
              className="bop-btn bop-btn-secondary"
              disabled={!exportPossibile}
              onClick={() => void esporta('totali')}
              title="Scarica in Excel la tabella dei totali, con i filtri correnti"
            >
              {esportando === 'totali' ? 'Esportazione…' : 'Excel totali'}
            </button>
            <button
              className="bop-btn bop-btn-secondary"
              disabled={!exportPossibile}
              onClick={() => void esporta('righe')}
              title="Scarica in Excel tutte le righe di documento che compongono la tabella dei totali"
            >
              {esportando === 'righe' ? 'Esportazione…' : 'Excel righe'}
            </button>
          </div>
        )}
      </div>

      <p className="bop-text-muted">{introduzione}</p>

      <div className="bop-tabs">
        <button className={`bop-tab ${tab === 'da-collegare' ? 'active' : ''}`} onClick={() => setTab('da-collegare')}>
          Da collegare a commessa
        </button>
        <button className={`bop-tab ${tab === 'analisi' ? 'active' : ''}`} onClick={() => setTab('analisi')}>
          Analisi per {controparte.etichetta.toLowerCase()}
        </button>
      </div>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaSel} onChange={(e) => setSocietaId(e.target.value)}>
            <option value="">Seleziona società</option>
            {listaSocieta.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}

        {tab === 'analisi' && (
          <select className="bop-select" value={vistaSel} onChange={(e) => setVista(e.target.value as VistaGo)}>
            {viste.map((v) => <option key={v.valore} value={v.valore}>{v.etichetta} — {v.nota}</option>)}
          </select>
        )}

        <FiltroPeriodoGo
          periodo={periodoPerVista(periodoSel, vistaElencoSel)}
          onChange={setPeriodo}
          dateAmmesse={DATE_PER_VISTA[vistaElencoSel]}
        />

        {societaSel && (
          <div className="bop-analisi-go__filtro-nominativo">
            <ComboFiltro
              label={controparte.etichetta}
              value={controparteSel || null}
              onChange={(id) => setSceltaControparte({ societaId: societaSel, codice: id ?? '' })}
              items={controparti}
            />
          </div>
        )}

        {tab === 'analisi' && (
          <>
            <select className="bop-select" value={raggruppaSel} onChange={(e) => setRaggruppa(e.target.value as RaggruppaGo)}>
              {raggruppamenti.map((r) => <option key={r.valore} value={r.valore}>Per {r.etichetta.toLowerCase()}</option>)}
            </select>

            <select className="bop-select" value={modoCommessaSel} onChange={(e) => setModoCommessa(e.target.value as ModoCommessa)}>
              <option value="tutte">Tutte le righe</option>
              <option value="solo">Solo su commessa</option>
              <option value="senza">Solo senza commessa</option>
            </select>

            <input
              className="bop-input"
              type="number"
              min="0"
              placeholder="Importo minimo"
              value={importoMinimoSel}
              onChange={(e) => setImportoMinimo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applica(); }}
            />
          </>
        )}

        {sulCicloAttivo && (
          <label className="bop-analisi-go__toggle">
            <input
              type="checkbox"
              checked={escludiAutofattureSel}
              onChange={(e) => setEscludiAutofatture(e.target.checked)}
            />
            Escludi autofatture
          </label>
        )}

        <BottoneCercaGo
          cercato={cercato}
          modificati={modificati}
          disabled={!societaSel || caricando}
          onClick={applica}
        />
      </div>

      <BopLoaderOverlay visibile={caricando} testo="Lettura dei dati da Gestionale Open…" />

      {societaId && !cercato && <InvitoCercaGo />}

      {tab === 'da-collegare' && (
        <TabDaCollegare
          ciclo={ciclo}
          societaId={societaId}
          periodo={periodo}
          escludiAutofatture={escludiAutofatture}
          controparteCodice={controparteCodice}
          versione={versione}
          cercato={cercato}
        />
      )}

      {tab === 'analisi' && (
        <>
          <AvvisiStatoGo societaId={societaId} stato={stato} avviso={avviso} />

          {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

          {societaId && stato === 'OK' && totali && (
            <>
              <div className="bop-analisi-go__kpi">
                <div className="bop-analisi-go__stat">
                  <div className="bop-analisi-go__stat-label">Totale {sulCicloAttivo ? 'ricavi' : 'costi'}</div>
                  <div className="bop-analisi-go__stat-value">{euro(complessivo?.importo)}</div>
                  <div className="bop-analisi-go__stat-sub">
                    {complessivo?.documenti ?? 0} documenti · {complessivo?.righe ?? 0} righe
                  </div>
                </div>

                <div className="bop-analisi-go__stat">
                  <div className="bop-analisi-go__stat-label">Attribuito a commessa</div>
                  <div className="bop-analisi-go__stat-value">{euro(complessivo?.importo_con_commessa)}</div>
                  <div className="bop-analisi-go__stat-sub">
                    {quotaCommessa !== null ? `${quotaCommessa}% del totale` : '—'}
                  </div>
                </div>

                <div className="bop-analisi-go__stat">
                  <div className="bop-analisi-go__stat-label">{controparte.etichetta === 'Cliente' ? 'Clienti' : 'Fornitori'} nel periodo</div>
                  <div className="bop-analisi-go__stat-value">{totali.count}</div>
                  <div className="bop-analisi-go__stat-sub">gruppi nell'aggregato corrente</div>
                </div>

                {sulCicloAttivo && !escludiAutofatture && (
                  <div className="bop-analisi-go__stat bop-analisi-go__stat--attenzione">
                    <div className="bop-analisi-go__stat-label">di cui autofatture</div>
                    <div className="bop-analisi-go__stat-value">{euro(complessivo?.importo_autofatture)}</div>
                    <div className="bop-analisi-go__stat-sub">non sono ricavi: contropartita di acquisti</div>
                  </div>
                )}
              </div>

              <p className="bop-analisi-go__nota">
                Database <strong>{totali.database ?? '—'}</strong> · date riferite a{' '}
                <strong>{etichettaDataRiferimento(totali.data_riferimento)}</strong>
                {annoParziale && ` · il ${periodo.anno} è parziale: accostato a un anno intero il confronto misura il calendario, non l'andamento`}
              </p>

              <BopExcelTable<GruppoTotali>
                data={totali.data}
                columns={colonne}
                rowKey={(r) => r.codice ?? r.etichetta ?? 'senza-codice'}
                loading={caricando}
                onRowClick={(r) => setGruppoAperto(r)}
                initialSort={{ key: 'importo', dir: 'desc' }}
                storageKey={`analisi-go-${ciclo}`}
                emptyState={<div className="bop-empty-state">Nessun dato per i filtri scelti</div>}
              />
            </>
          )}

          {gruppoAperto && (
            <ModaleDocumentiGo
              titolo={gruppoAperto.etichetta ?? gruppoAperto.codice ?? '(non indicato)'}
              filtri={filtriGruppo(gruppoAperto)}
              filtroDocumenti={filtroMese}
              conDaAttribuire={!suOrdinato}
              onClose={() => setGruppoAperto(null)}
            />
          )}
        </>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
